import { useTheme } from '@mui/material/styles';
import type * as ChartJs from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import React from 'react';
import { Bar } from 'react-chartjs-2';

import { useIsMobile } from '@hooks/useIsMobile';

import '../register';

export type Dataset = ChartJs.ChartDataset<'bar', number[]>;
export type ChartData = ChartJs.ChartData<'bar', number[]>;

interface StackedBarChartProps {
  chartData: ChartData;
}

const defaultDatasetConfig: Dataset = {
  barPercentage: 0.4,
  categoryPercentage: 1,
  borderWidth: 0,
  label: '',
  barThickness: 30,
  data: [],
};

export default function StackedBarChart({ chartData }: StackedBarChartProps) {
  const theme = useTheme();
  const isTablet = useIsMobile('lg');
  const isMediumSize = useIsMobile('md');
  const isMobile = useIsMobile('sm');

  const aspectRatio = React.useMemo(() => {
    switch (true) {
      case window.innerWidth >= 1201:
      case isMobile:
        return 8;
      case isMediumSize:
        return 13;
      case isTablet:
        return 22;
      default:
        return 1;
    }
  }, [isMediumSize, isMobile, isTablet]);

  const data: ChartData = {
    labels: ['default'],
    ...chartData,
    datasets: chartData.datasets.map((dataset, index) => {
      const isFirstItem = index === 0;
      const isLastItem = index === chartData.datasets.length - 1;
      const rounding = 20;

      const borderRadius: ChartJs.BorderRadius = {
        bottomLeft: 0,
        bottomRight: 0,
        topLeft: 0,
        topRight: 0,
      };
      if (isFirstItem) {
        borderRadius.topLeft = rounding;
        borderRadius.bottomLeft = rounding;
      }
      if (isLastItem) {
        borderRadius.topRight = rounding;
        borderRadius.bottomRight = rounding;
      }

      return {
        ...defaultDatasetConfig,
        ...dataset,
        borderRadius,
        borderSkipped: false,
      };
    }),
  };

  const options: ChartJs.ChartOptions<'bar'> = {
    aspectRatio,
    maintainAspectRatio: true,
    animations: {
      x: {
        delay: 0,
        duration: 600,
      },
      y: {
        delay: 0,
        duration: 600,
      },
    },
    layout: {
      padding: {
        top: 20,
        right: 15,
        left: 15,
      },
    },
    animation: {
      duration: 1000,
      onComplete: function ({ chart }) {
        const ctx = chart.ctx;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
      },
    },
    plugins: {
      datalabels: {
        color: (context) => {
          const { dataset } = context;
          if (typeof dataset.backgroundColor === 'string') {
            return dataset.backgroundColor;
          }
          return theme.palette.text.secondary;
        },
        align: 'top',
        anchor: 'center',
        offset: 10,
        formatter: (value) => {
          try {
            const parsedValue =
              typeof value === 'string' ? parseFloat(value) : value;
            // Show integer if value is a whole number, otherwise show 1 decimal
            return parsedValue % 1 === 0
              ? `${parsedValue}%`
              : `${parsedValue.toFixed(1)}%`;
          } catch {
            return `${value}%`;
          }
        },
        font: {
          family: theme.typography.fontFamily,
          size: theme.typography.fontSize,
          weight: 'bolder',
        },
      },
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    indexAxis: 'y',
    scales: {
      x: {
        stacked: true,
        display: false,
      },
      y: {
        stacked: true,
        display: false,
      },
    },
  };

  return <Bar data={data} options={options} plugins={[ChartDataLabels]} />;
}
